import { useEffect, useMemo, useState } from "react";
import { Stack, Button, Typography, CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Add, ExpandLess } from "@material-ui/icons";
import { ViewContactDialog } from "components/_dashboard/contacts/table/rows/ViewContactDialog";
import { getFavoritesListingsQuery } from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import { EditContactDialog } from "components/_dashboard/contacts/modal/EditContactDialog";
import { useModal } from "hooks/useModal";
import useAuth from "hooks/useAuth";
import useContactByPhone from "hooks/contacts/useContactByPhone";
import useSettings from "hooks/useSettings";
import { getFullName } from "utils/Haim/utils";
import { ContactTableAvatar } from "../contacts/table/rows/ContactTableAvatar";
import CrmHandlerBadge from "../crm/handler/CrmHandlerBadge";
import useContactHandler from "hooks/crm/useContactHandler";
import { preventBubbling } from "utils/listings/helper";
import type { RealEstateContact, FavoriteListingsOfContact } from "../../../@types/sharedSchema";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils"; 
import { buildAttendeeContactSeed } from "utils/calendar/helper";

type CalendarCustomerCardProps = {
  open: boolean;
  attendee: RealEstateContact;
  propertyTypes?: string[];
};

export function CalendarCustomerCard({
  open,
  attendee,
  propertyTypes
}: CalendarCustomerCardProps): JSX.Element {
  const [customerCardOpen, setCustomerCardOpen] = useState(false);

  const [favoriteListings, setFavoriteListings] = useState<null | FavoriteListingsOfContact>(null);
  const [loadingListingMatches, setLoadingListingMatches] = useState(true);

  const { t: translate } = useTranslation();

  const { user } = useAuth();

  const { themeDirection } = useSettings();

  const {
    open: editContactDialogOpen,
    openModal: openEditContactDialog,
    closeModal: closeEditContactDialog
  } = useModal();

  // An attendee reaches this card as a contact-shaped object whose `id` IS the phone
  // (`parseAttendeesToContacts` puts `phoneNumberInternational` there). Never
  // `callerPhoneNumber` — that field is absent on ~90% of real contact documents, and
  // this card is handed one whenever the attendee is already in the book.
  const attendeePhone = attendee?.id;

  // Is this attendee in the book, and if so who: the contact's own Firestore document, live
  // — one single-document listener per card, rather than the realtor's whole book downloaded
  // to find one entry in it. It has to be the document and not the lookup layer's
  // `ContactSummary`: it is merged into what `EditContactDialog` opens on below, and a form
  // opened on a projection writes the projection back.
  //
  // `isContact === undefined` is "not answered yet", NOT "not in the book": the row waits
  // rather than offer "add to contacts" for somebody who may already be one.
  const { exists: isContact, contact } = useContactByPhone(attendeePhone);
  const isResolvingContact = isContact === undefined;
  // The live document is a new object on every snapshot; its id is what stays put.
  const contactId = contact?.id;

  /**
   * Is a COLLEAGUE designated to handle this attendee?
   *
   * Shares the module store with every other surface, so on a page whose picker already
   * asked about this phone it issues nothing. `undefined` (pending or failed) and a
   * designation naming the viewer both render as nothing — never tell a user about
   * themselves, and never state "nobody" from an unanswered lookup.
   */
  const { handler: attendeeHandler } = useContactHandler(attendeePhone ?? null);
  const collidingHandler = attendeeHandler && !attendeeHandler.isMe && !attendeeHandler.stale ? attendeeHandler : null;

  useEffect(() => {
    if (!open) return;

    if (!contactId) return;

    setLoadingListingMatches(true);

    const favoriteListingsSnapshot = getFavoritesListingsQuery(user?.email, contactId).onSnapshot(
      (snapshot) => {
        const listingMatchesData = pickMinisitePage(snapshot.docs)?.data() ?? null;

        setFavoriteListings(listingMatchesData);
        setLoadingListingMatches(false);
      }
    );

    return favoriteListingsSnapshot;
    // Keyed on the id: re-subscribing on every snapshot of the contact would put this row
    // back on its spinner each time the contact is written, from anywhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const toggleCustomerCardOpen = (): void => setCustomerCardOpen(!customerCardOpen);

  // Nothing to open yet: either the lookup has not answered, or it found the contact and
  // their favourite listings are still on the way.
  const isRowBusy = isResolvingContact || loadingListingMatches;

  const parsedContact = {
    ...attendee,
    ...contact
  };

  /**
   * Which mode the dialog opens in — the same split as the calls card (`CallsTableCustomerCard`).
   *
   * CREATE ("הוסף את … לרשימת הלקוחות"): opens on `buildAttendeeContactSeed` — the phone in
   * create mode's own channel. Handing the dialog the attendee itself opened it with an empty,
   * required phone box for the number shown on the button just pressed.
   *
   * EDIT (the contact card's edit button): the real document. The unconditional
   * `forceCreation` this replaces put an existing contact into create mode, where its own
   * phone read as a duplicate.
   *
   * State, not `!contact`: the live lookup attaches the new contact the moment the create
   * succeeds, and a derived flag would flip the still-open dialog into an edit form.
   */
  const [creatingContact, setCreatingContact] = useState(false);

  // Keyed on the primitives: the form re-parses its attendees into new objects every render.
  const seededContact = useMemo(
    () =>
      buildAttendeeContactSeed({
        id: attendeePhone,
        callerFirstName: attendee?.callerFirstName,
        callerLastName: attendee?.callerLastName
      }),
    [attendeePhone, attendee?.callerFirstName, attendee?.callerLastName]
  );

  const handleCreateContact = (): void => {
    setCreatingContact(true);
    openEditContactDialog();
  };

  const handleEditContact = (): void => {
    setCreatingContact(false);
    openEditContactDialog();
  };

  const isRTL = themeDirection === "rtl";

  const parsedPhoneNumber =
    parsePhoneNumber(attendeePhone)?.formatNational() ?? attendeePhone;

  const customerName =
    getFullName(parsedContact?.callerFirstName, parsedContact?.callerLastName) || parsedPhoneNumber;

  return (
    <Stack gap={1} onSubmit={preventBubbling()}>
      <EditContactDialog
        open={editContactDialogOpen}
        contact={creatingContact ? seededContact : contact}
        propertyTypes={propertyTypes}
        forceCreation={creatingContact}
        onClose={closeEditContactDialog}
      />
      {/* While the lookup is unanswered this is the row too — spinner, disabled — so the
          card never offers "add to contacts" for an attendee it has not looked up yet. */}
      {contact || isResolvingContact ? (
        <Button
          sx={{
            color: "text.primary",
            justifyContent: "flex-start",
            "&:hover": {
              backgroundColor: "rgba(145, 158, 171, 0.08)"
            }
          }}
          startIcon={
            isRowBusy ? (
              <CircularProgress size={18} />
            ) : (
              <ExpandLess
                sx={{
                  transition: "transform 200ms",
                  transform: customerCardOpen
                    ? "rotate(0deg)"
                    : isRTL
                    ? "rotate(-90deg)"
                    : "rotate(90deg)"
                }}
              />
            )
          }
          disabled={isRowBusy}
          onClick={toggleCustomerCardOpen}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <ContactTableAvatar
              sx={{ fontSize: 12 }}
              avatarSize={24}
              callerFullName={customerName}
            />
            <Typography variant="subtitle1">{customerName}</Typography>
            {/* A CAPTION HERE, NOT A DIALOG. The guard for a meeting or a task fires at the
                ENTRY POINT — `ContactQuickActionsMenu` / the CRM row menu — before this
                editor ever opens, so by the time an attendee card is on screen the confirm
                has already happened. A second modal over a half-filled form is the annoying
                kind of guardrail. This is the reminder, for an attendee added from inside
                the form, where no entry-point guard ran. */}
            {collidingHandler && (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                <CrmHandlerBadge handler={collidingHandler} size="xs" reserve={false} />
                <Typography variant="caption" noWrap sx={{ color: "warning.dark", minWidth: 0 }}>
                  {`מטפל: ${collidingHandler.name}`}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Button>
      ) : (
        <Button
          sx={{
            color: "text.primary",
            justifyContent: "flex-start",
            "&:hover": {
              backgroundColor: "rgba(145, 158, 171, 0.08)"
            }
          }}
          title={attendeePhone}
          startIcon={<Add />}
          onClick={handleCreateContact}
        >
          {translate("calendarPage.control.addPhoneNumberToContact", {
            phoneNumber: parsedPhoneNumber
          })}
        </Button>
      )}
      {contact && (
        <ViewContactDialog
          open={customerCardOpen}
          contact={contact}
          favoriteListingsOfContact={favoriteListings}
          handleEditContactDialogOpen={handleEditContact}
        />
      )}
    </Stack>
  );
}
